import type { TError } from '@onesource/schemas';
import { useQuery } from '@tanstack/react-query';

import axios from '../../axios';
import { ENTITY } from '../entity';
import type { TQuizQuestion } from './get-quiz';

/**
 * Quiz Answer with grading result and question details
 */
export interface TQuizAnswerWithQuestion {
  id: string;
  attemptId: string;
  questionId: string;
  selectedOptionIds: string[];
  orderingSequence: string[];
  textAnswer: string | null;
  isCorrect: boolean | null;
  pointsAwarded: number;
  gradedAt: string | null;
  feedback: string | null;
  question: TQuizQuestion;
}

/**
 * Attempt result with answers and question details
 */
export interface TAttemptResult {
  id: string;
  quizId: string;
  userId: string;
  attemptNumber: number;
  status: 'in_progress' | 'submitted' | 'graded' | 'abandoned';
  scorePercent: number | null;
  totalPoints: number | null;
  earnedPoints: number | null;
  passed: boolean | null;
  startedAt: string;
  submittedAt: string | null;
  gradedAt: string | null;
  gradedBy: string | null;
  timeSpentSeconds: number | null;
  answers: TQuizAnswerWithQuestion[];
  quiz?: {
    id: string;
    title: string;
    showCorrectAnswers: boolean;
    maxAttempts: number | null;
  };
}

/**
 * Query key for fetching a specific attempt by ID
 */
export const useGetAttemptQueryKey = (id: string) => [ENTITY, 'attempt', id];

/**
 * Hook to fetch a specific attempt by ID with answers and question details
 *
 * @param id - Attempt ID (UUID)
 */
export const useGetAttempt = (id: string) => {
  return useQuery<TAttemptResult, TError>({
    queryKey: useGetAttemptQueryKey(id),
    queryFn: async () => {
      const res = await axios.get<TAttemptResult>(`/community-hub/attempts/${id}`);
      return res.data;
    },
    enabled: !!id,
  });
};
